<script setup name="Template2">
import { ref, onMounted, onUnmounted, nextTick, inject, watch } from "vue";
import ContainerCom from "./ContainerCom.vue";
import TextCom from "./TextCom.vue";
import { ElMessageBox } from "element-plus";
import api from "../api/index";
import { useI18n } from "vue-i18n";

const headerLineRef = ref(null);
const sectionRef = ref(null);
// 记录上一次滚动位置
let lastScrollTop = 0;
// 记录元素初始位置
let initialLeft = 0;
// 滚动速度系数，控制移动速度
const scrollSpeed = 0.5;
// 记录section元素的初始位置
let sectionOffsetTop = 0;
// 记录section元素的高度
let sectionHeight = 0;

// 处理滚动事件
const handleScroll = () => {
  if (!headerLineRef.value || !sectionRef.value) return;

  // 获取当前滚动位置
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  // 计算section元素相对于视口的位置
  const sectionTop = sectionOffsetTop - scrollTop;

  // 当section元素进入视口时（顶部进入视口）
  if (sectionTop < window.innerHeight && sectionTop + sectionHeight > 0) {
    // 计算section元素顶部与视口顶部的距离
    const distanceFromTop = Math.max(0, window.innerHeight - sectionTop);

    // 计算滚动距离和方向
    const scrollDistance = scrollTop - lastScrollTop;
    const scrollDirection = scrollDistance > 0 ? "down" : "up";

    // 获取元素当前位置
    const currentTransform = window.getComputedStyle(
      headerLineRef.value,
    ).transform;
    const matrix = new DOMMatrix(currentTransform);
    let translateX = matrix.m41;

    // 根据滚动方向调整元素位置
    if (scrollDirection === "up") {
      // 向下滚动，向右移动
      translateX += Math.abs(scrollDistance) * scrollSpeed;
      // 限制向右移动最多300px
      translateX = Math.min(translateX, 200);
    } else {
      // 向上滚动，向左移动
      translateX -= Math.abs(scrollDistance) * scrollSpeed;
      // 限制向左移动不超过初始位置
      translateX = Math.max(translateX, 0);
    }

    // 应用新位置
    headerLineRef.value.style.transform = `translateX(${translateX}px)`;
  } else if (sectionTop >= window.innerHeight) {
    // 当section元素完全在视口下方时，重置位置
    headerLineRef.value.style.transform = "translateX(200px)";
  }

  // 更新上一次滚动位置
  lastScrollTop = scrollTop;
};

// 组件挂载时添加滚动事件监听
onMounted(() => {
  if (headerLineRef.value && sectionRef.value) {
    // 记录初始位置
    initialLeft = headerLineRef.value.offsetLeft;
    // 获取section元素的位置和高度
    const rect = sectionRef.value.getBoundingClientRect();
    sectionOffsetTop = rect.top + window.pageYOffset;
    sectionHeight = rect.height;
  }
  window.addEventListener("scroll", handleScroll);
});

// 组件卸载时移除滚动事件监听
onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
// 从App.vue中注入内容映射更新函数
const updateContentMap = inject("updatecontentMap", () => {});
// 为模板组件定义属性
const { t } = useI18n();
const props = defineProps({
  templateId: {
    type: String,
    default: "",
  },
});
const filteredContents = ref({});
const selectedTab = ref("");
const isLoggedIn = inject("isLoggedIn", false);
const contentMap = inject("contentMap", {});
// 鼠标拖动滚动相关
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const scrollbarRef = ref(null);
// 处理删除事件
const handleDelete = (contentId) => {
  emit("handleDelete", props.templateId);
};
watch(
  () => contentMap.value,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      getData();
    }
  },
);
const updateOb = () => {
  // 获取所有需要观察的子容器元素
  const items = document.querySelectorAll(".sub-container-item");
  items.forEach((item) => {
    // 初始设置为不可见状态
    item.classList.add("invisible");
    // 开始观察元素
    observer.observe(item);
  });
};
const getData = async () => {
  try {
    filteredContents.value = {};
    const res = await api.content.getContentsByTemplate(props.templateId);
    if (res.success) {
      // 处理返回的内容数据,过滤标题和背景content

      const tabs = res.data
        .filter(
          (item) =>
            item.contentId.includes("-tab-") &&
            !item.contentId.includes("-video") &&
            !item.contentId.includes("-text"),
        )
        .sort((a, b) => {
          const aNum = parseInt(contentMap.value[a.contentId].orderNum || 0);
          const bNum = parseInt(contentMap.value[b.contentId].orderNum || 0);
          return aNum - bNum;
        });
      tabs.forEach((i) => {
        const set = new Set(
          res.data
            .filter(
              (item) =>
                item.contentId.includes(i.contentId) &&
                item.contentId !== i.contentId,
            )
            .map((item) => item.contentId.split("-").pop()),
        );
        const arr = Array.from(set).sort((a, b) => {
          const aNum = parseInt(
            contentMap.value[i.contentId + "-video-" + a].orderNum || 0,
          );
          const bNum = parseInt(
            contentMap.value[i.contentId + "-video-" + b].orderNum || 0,
          );
          return aNum - bNum;
        });
        filteredContents.value[i.contentId] = [...arr];
      });
      if (!selectedTab.value) {
        selectedTab.value = tabs[0].contentId;
      }
      nextTick(() => {
        updateOb();
      });
    }
  } catch (error) {
    console.log(error);
  }
};
const handleAdd = () => {
  api.product.addContents(props.templateId).then((res) => {
    if (res.success) {
      updateContentMap();
    }
  });
};
const handleTabsAdd = () => {
  api.product
    .addTabsContents(props.templateId, { tabId: selectedTab.value })
    .then((res) => {
      if (res.success) {
        updateContentMap();
      }
    });
};
const handleDeleteContent = (contentId) => {
  ElMessageBox.confirm(
    t("product.page.deleteConfirm"),
    t("product.page.deleteConfirmTitle"),
    {
      confirmButtonText: t("product.page.confirm"),
      cancelButtonText: t("product.page.cancel"),
      type: "warning",
    },
  )
    .then(() => {
      //调用删除模板接口
      const data = [
        selectedTab.value + "-video-" + contentId,
        selectedTab.value + "-text-" + contentId,
      ];
      api.content.deleteContentsBatch({ ids: data }).then((res) => {
        if (res.success) {
          updateContentMap();
        }
      });
    })
    .catch(() => {
      // 用户点击了取消，不执行任何操作
    });
};

// 鼠标拖动滚动事件处理
const handleMouseDown = (e) => {
  // 直接获取滚动容器
  const scrollContainer = scrollbarRef.value?.$el.querySelector(
    ".el-scrollbar__wrap",
  );
  if (!scrollContainer) return;

  isDragging.value = true;
  // 记录当前鼠标位置和滚动位置
  startX.value = e.clientX;
  scrollLeft.value = scrollContainer.scrollLeft;

  // 定义移动事件处理函数
  const handleDragMove = (moveEvent) => {
    if (!isDragging.value) return;
    moveEvent.preventDefault();

    // 计算鼠标移动距离
    const deltaX = moveEvent.clientX - startX.value;
    // 计算新的滚动位置
    const newScrollLeft = scrollLeft.value - deltaX;
    // 设置滚动位置
    scrollContainer.scrollLeft = newScrollLeft;
  };

  // 定义清理函数
  const cleanup = () => {
    isDragging.value = false;
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", cleanup);
    document.removeEventListener("mouseleave", cleanup);
  };

  // 添加事件监听
  document.addEventListener("mousemove", handleDragMove);
  document.addEventListener("mouseup", cleanup);
  document.addEventListener("mouseleave", cleanup);

  e.preventDefault();
};

// 移除其他鼠标事件处理函数，因为已经在handleMouseDown中处理了
const handleMouseMove = () => {};
const handleMouseUp = () => {};
const handleMouseLeave = () => {};

// 定义emits
const emit = defineEmits(["edit", "handleDelete"]);

// 存储Intersection Observer实例
let observer = null;
const handleDeleteTab = (tabId) => {
  ElMessageBox.confirm(
    t("product.page.deleteConfirm"),
    t("product.page.deleteConfirmTitle"),
    {
      confirmButtonText: t("product.page.confirm"),
      cancelButtonText: t("product.page.cancel"),
      type: "warning",
    },
  )
    .then(() => {
      const data = [tabId];
      const contentIds = filteredContents.value[tabId];
      contentIds.forEach((i) => {
        data.push(tabId + "-video-" + i);
        data.push(tabId + "-text-" + i);
      });
      //调用删除模板接口
      api.content.deleteContentsBatch({ ids: data }).then((res) => {
        if (res.success) {
          updateContentMap();
        }
      });
    })
    .catch(() => {
      // 用户点击了取消，不执行任何操作
    });
};
// 监听组件挂载
onMounted(() => {
  getData();
  // 创建Intersection Observer实例
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 当元素可见度达到20%时，添加可见类
          entry.target.classList.add("visible");
          entry.target.classList.remove("invisible");
          // 移除unobserve调用，允许元素每次进入视口都触发动画
        } else {
          // 当元素完全离开视口时，重置为不可见状态，允许再次进入时触发动画
          // 添加防抖机制，只有当元素完全离开视口时才重置
          if (entry.intersectionRatio <= 0) {
            entry.target.classList.add("invisible");
            entry.target.classList.remove("visible");
          }
        }
      });
    },
    {
      // 设置阈值为20%，即元素20%可见时触发
      threshold: 0,
      // 添加一些margin来提前触发，改善用户体验
      rootMargin: "0px 0px 100px 0px",
    },
  );
});

// 监听组件卸载，清理Observer
onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>
<template>
  <div class="template-container8" ref="sectionRef">
    <el-icon
      class="order-number"
      color="#222"
      size="20"
      @click="emit('order', $event)"
      v-if="isLoggedIn"
    >
      <DCaret />
    </el-icon>
    <el-icon class="delete-button" @click="handleDelete" v-if="isLoggedIn">
      <Delete />
    </el-icon>
    <div ref="headerLineRef" class="technology-header-line">Videos</div>
    <div class="first-row">
      <TextCom
        :floatAnimationEnabled="false"
        :contentId="props.templateId + '-text-1'"
        :fontScale="1"
      />
      <TextCom
        class="tab-item"
        :class="{ 'tab-item-active': selectedTab === key }"
        v-for="(value, key) in filteredContents"
        :key="key"
        :floatAnimationEnabled="false"
        :contentId="key"
        :showDelete="true"
        :showOrderNum="true"
        @delete="handleDeleteTab(key)"
        @order="emit('contentOrder', $event, key)"
        @click="selectedTab = key"
      />
      <el-icon v-if="isLoggedIn" class="tab-item-icon" @click="handleAdd">
        <Plus />
      </el-icon>
    </div>

    <el-scrollbar class="sub-container" ref="scrollbarRef">
      <div class="sub-container-wrapper" @mousedown="handleMouseDown">
        <div
          class="sub-container-item"
          v-for="i in filteredContents[selectedTab]"
          :key="i"
        >
          <div class="sub-container-item-video">
            <ContainerCom
              :contentId="selectedTab + '-video-' + i"
              :showDelete="true"
              :showOrderNum="true"
              :autoPlay="false"
              @order="emit('contentOrder', $event, selectedTab + '-video-' + i)"
              @delete="handleDeleteContent(i)"
              backgroundSize="cover"
              backgroundPosition="center"
            >
            </ContainerCom>
          </div>

          <TextCom
            :floatAnimationEnabled="false"
            :contentId="selectedTab + '-text-' + i"
            :fontScale="0.8"
          />
        </div>
        <div class="sub-container-item">
          <div
            v-if="isLoggedIn"
            class="sub-container-item-video sub-container-item-add"
            @click="handleTabsAdd"
          >
            <el-icon class="sub-container-item-video-icon">
              <Plus />
            </el-icon>
          </div>
        </div>
      </div>
    </el-scrollbar>
  </div>
  <!-- 使用ContainerCom作为主容器 -->
</template>

<style scoped>
/* 基础样式，保持PC端原有样式不变 */
.template-container8 {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 3.5vw 10vw;
}
.technology-header-line {
  font-size: 8rem;
  font-weight: bold;
  color: #dde3e8;
  cursor: default;
  position: absolute;
  top: 0;
  right: 0;
}
.first-row {
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 1.5vw;
}
.order-number {
  position: absolute;
  top: 20px;
  right: 40px;
  border: none;
  padding: 2px;
  /* padding: 8px 16px; */
  border-radius: 4px;
  cursor: pointer;
  /* font-size: 14px; */
  transition: all 0.3s ease;
  z-index: 1000;
  margin-right: 8px;
  background-color: #fff;
}
.order-number:hover {
  background-color: #fff;
  transform: scale(1.05);
}

.sub-container {
  width: 100%;
  height: auto;
  padding-bottom: 2vw;
}

/* el-scrollbar 自定义样式 */
:deep(.el-scrollbar__wrap) {
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
}

:deep(.el-scrollbar__bar) {
  height: 2px;
}

:deep(.el-scrollbar__bar.is-horizontal) {
  background: transparent;
}

:deep(.el-scrollbar__thumb) {
  background: #222;
  border-radius: 3px;
}

:deep(.el-scrollbar__thumb:hover) {
  background: #444;
}

/* 内部内容容器 */
.sub-container-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 0;
}
.sub-container-item + .sub-container-item {
  margin-left: 2.0833vw;
}
.sub-container-item-add {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.sub-container-item-add:hover {
  background-color: #f5f5f5;
  i {
    color: #4a90e2;
  }
}
.delete-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: #f56c6c;
  color: #fff;
  border: none;
  padding: 2px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 20px;
  transition: all 0.3s ease;
  z-index: 1000;
  /* 移动端适配：调整大小和位置 */
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    font-size: 18px;
  }
}

.sub-container-item-video {
  margin-top: 2.0833vw;
  margin-bottom: 2.0833vw;
  height: 18.5417vw;
  width: 31.7708vw;
  border-radius: 1.0417vw;
  overflow: hidden;
  border: 1px solid #e2e2e2;
}

.tab-item {
  text-align: center;
  margin-left: 1.0833vw;
  cursor: pointer;
  position: relative;
  padding-bottom: 0.5vw;
  transition: color 0.3s ease;
}
.tab-item-icon {
  margin-left: 1.0833vw;
  cursor: pointer;
  position: relative;
  padding-bottom: 0.5vw;
  transition: color 0.3s ease;
}
.tab-item-icon:hover {
  color: #4a90e2;
  /* 以中心旋转90度 */
}

.tab-item-active {
  color: #4a90e2;
}

.tab-item-active::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 0.2vw;
  background-color: #4a90e2;
  border-radius: 0.1vw;
  transition: all 0.3s ease;
}

/* 移动端适配 */
@media (max-width: 1024px) {
  .template-container8 {
    padding: 5vw 5vw;
  }

  .technology-header-line {
    font-size: 6rem;
  }

  .first-row {
    flex-wrap: wrap;
    gap: 1vw;
  }

  .tab-item {
    margin-left: 1.5vw;
    margin-top: 1vw;
  }

  .sub-container-item-video {
    height: 25vw;
    width: 40vw;
    margin-top: 1.5vw;
    margin-bottom: 1.5vw;
    border-radius: 1.5vw;
  }

  .sub-container-item + .sub-container-item {
    margin-left: 1.5vw;
  }
}

@media (max-width: 768px) {
  .template-container8 {
    padding: 6vw 4vw;
  }

  .technology-header-line {
    font-size: 4rem;
  }

  .first-row {
    margin-bottom: 2vw;
  }

  .tab-item {
    margin-left: 1.5vw;
    margin-top: 1.5vw;
    font-size: 0.9rem;
  }

  .tab-item-active::after {
    height: 2px;
  }

  .sub-container {
    padding-bottom: 3vw;
  }

  .sub-container-item-video {
    height: 30vw;
    width: 50vw;
    margin-top: 2vw;
    margin-bottom: 2vw;
    border-radius: 2vw;
  }

  .sub-container-item + .sub-container-item {
    margin-left: 2vw;
  }

  /* 确保横向滚动在移动端流畅 */
  :deep(.el-scrollbar__wrap) {
    -webkit-overflow-scrolling: touch;
  }
}

@media (max-width: 480px) {
  .technology-header-line {
    font-size: 3rem;
  }

  .first-row {
    margin-bottom: 3vw;
    gap: 2vw;
  }

  .tab-item {
    margin-left: 0;
    margin-top: 2vw;
    font-size: 0.8rem;
  }

  .sub-container-item-video {
    height: 40vw;
    width: 70vw;
    margin-top: 3vw;
    margin-bottom: 3vw;
    border-radius: 2.5vw;
  }

  .sub-container-item + .sub-container-item {
    margin-left: 3vw;
  }
}

/* 确保ContainerCom内部的视频能正确显示 */
.sub-container-item-video :deep(video) {
  /* 确保视频能自适应容器 */
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}
</style>
